import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { EvidenceCard, metricRows, OrderNote, ReadingNote, TallyNote } from '@/pages/anatel/conselho/shared';
import { ContestedItemsTable } from '@/pages/anatel/conselho/ContestedItemsTable';
import { IntervalList, type IntervalRow } from '@/pages/anatel/conselho/IntervalList';
import { RunFooter } from '@/pages/anatel/conselho/RunFooter';
import { RubricTable } from '@/pages/anatel/conselho/RubricTable';
import type { PersonConselho } from '@/services/types';

/**
 * The model-derived block of one member's page.
 *
 * Every Answer here is rendered at scope="record": the track exists for the
 * whole agency, and an absence on this page is about THIS person (thin
 * record, no contested item) -- the substrate-code-vs-subject confusion this
 * repo has shipped five times, avoided by the prop.
 */
export function PersonConselhoPanels({ conselho }: { conselho: PersonConselho }) {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const signed = new Intl.NumberFormat(i18n.language, { signDisplay: 'exceptZero', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const percent = new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 1 });
  const logit = (v: number) => signed.format(v);
  // A scope row is keyed by the ETL (`party_class=large_incumbent`,
  // `subject=PADO: Fust`, `year=2021`, `relator=<name>`); the prefix is
  // translated, the value is the ETL's own label and stays as written.
  const scopePrefix: Record<string, string> = {
    party_class: t('anatel:conselheiro.conselho.scopePartyClass'),
    subject: t('anatel:conselheiro.conselho.scopeSubject'),
    year: t('anatel:conselheiro.conselho.scopeYear'),
    relator: t('anatel:conselheiro.conselho.scopeRelator'),
  };
  const scopeLabel = (rowName: string) => {
    if (!rowName.includes(' — ')) return t('anatel:conselheiro.conselho.overall');
    const scope = rowName.split(' — ')[1];
    const eq = scope.indexOf('=');
    if (eq < 0) return scope;
    const prefix = scopePrefix[scope.slice(0, eq)];
    return prefix ? `${prefix}: ${scope.slice(eq + 1)}` : scope;
  };
  // Every severity row opens the cases it rests on. The row key is
  // `${name}|${scope}` (metricRows), and the scope is the ETL's own string,
  // which the case list re-reads verbatim -- never the translated label.
  const casesLink = (row: IntervalRow) => {
    const scope = row.key.slice(row.key.lastIndexOf('|') + 1);
    if (!row.slug || !scope) return null;
    return `/anatel/conselho/cases?${new URLSearchParams({ member: row.slug, scope })}`;
  };

  return (
    <section className="space-y-4 rounded-lg border bg-card p-4" data-panel="conselho">
      <h2 className="text-sm font-semibold text-foreground">{t('anatel:conselheiro.conselho.title')}</h2>
      <Link
        to="/anatel/conselho/methodology"
        className="inline-block text-xs text-primary underline-offset-4 hover:underline"
        data-testid="methodology-link"
      >
        {t('common:conselhoMethodology.link')}
      </Link>
      <EvidenceCard evidence={conselho.evidence} />
      <ReadingNote reading={conselho.person_reading} />

      <div data-panel="conselho-severity">
        <h3 className="mb-2 text-xs font-semibold text-foreground">{t('anatel:conselheiro.conselho.severityTitle')}</h3>
        <Answer answer={conselho.severity} help={t('anatel:conselheiro.conselho.severityHelp')} scope="record">
          {(rows) => (
            <>
              <IntervalList
                rows={metricRows(rows).map((r) => ({ ...r, name: scopeLabel(r.name) }))}
                axisLabel={t('anatel:conselho.severity.axis')}
                format={logit}
                rawFormat={(v) => percent.format(v)}
                linkTo={casesLink}
              />
              <p className="mt-1 text-[11px] text-muted-foreground">{t('anatel:conselheiro.conselho.casesLink')}</p>
            </>
          )}
        </Answer>
        <TallyNote tally={conselho.severity_tally} />
        <RunFooter run={conselho.severity_run} />
      </div>

      <div data-panel="conselho-follow">
        <h3 className="mb-2 text-xs font-semibold text-foreground">{t('anatel:conselheiro.conselho.followTitle')}</h3>
        <Answer answer={conselho.follow} help={t('anatel:conselheiro.conselho.followHelp')} scope="record">
          {(rows) => (
            <IntervalList
              rows={metricRows(rows).map((r) => ({ ...r, name: scopeLabel(r.name) }))}
              axisLabel={t('anatel:conselho.dissent.followAxis')}
              format={logit}
              rawFormat={(v) => percent.format(v)}
            />
          )}
        </Answer>
        <TallyNote tally={conselho.follow_tally} />
        {conselho.followed ? (
          <div data-panel="conselho-followed">
            <h4 className="mb-2 mt-3 text-xs font-semibold text-foreground">{t('anatel:conselheiro.conselho.followedTitle')}</h4>
            <OrderNote order={conselho.followed_order} />
            <Answer answer={conselho.followed} help={t('anatel:conselho.followed.help')} scope="record">
              {(rows) => (
                <IntervalList
                  rows={metricRows(rows).map((r) => ({ ...r, name: scopeLabel(r.name) }))}
                  axisLabel={t('anatel:conselho.followed.axis')}
                  format={logit}
                  rawFormat={(v) => percent.format(v)}
                  rawLabel={t('anatel:conselho.followed.raw')}
                />
              )}
            </Answer>
          </div>
        ) : null}
        <RunFooter run={conselho.follow_run} />
      </div>

      <div data-panel="conselho-positions">
        <h3 className="mb-2 text-xs font-semibold text-foreground">{t('anatel:conselheiro.conselho.positionsTitle')}</h3>
        <Answer answer={conselho.positions} help={t('anatel:conselheiro.conselho.positionsHelp')} scope="record">
          {(rows) => (
            <IntervalList
              rows={rows.map((r) => ({
                key: r.period,
                name: r.period === 'all' ? t('anatel:conselheiro.conselho.overall') : r.period,
                n: r.n_contested,
                nLabel: t('anatel:conselho.positions.contested'),
                effect: r.position,
                lower: r.lower,
                upper: r.upper,
                suppressed: r.suppressed,
              }))}
              axisLabel={t('anatel:conselho.positions.axis')}
              format={logit}
            />
          )}
        </Answer>
        <RunFooter run={conselho.position_run} />
        <h3 className="mb-2 mt-3 text-xs font-semibold text-foreground">{t('anatel:conselheiro.conselho.propensityTitle')}</h3>
        <Answer answer={conselho.propensities} help={t('anatel:conselho.positions.propensityHelp')} scope="record">
          {(rows) => (
            <IntervalList
              rows={rows.map((r) => ({
                key: r.period,
                name: t('anatel:conselheiro.conselho.overall'),
                n: r.n_items,
                nLabel: t('anatel:conselho.positions.items'),
                effect: r.dissent_propensity,
                lower: r.lower,
                upper: r.upper,
                suppressed: r.suppressed,
              }))}
              axisLabel={t('anatel:conselho.positions.propensityAxis')}
              format={logit}
              zeroLine={false}
            />
          )}
        </Answer>
      </div>

      <div data-panel="conselho-style">
        <h3 className="mb-2 text-xs font-semibold text-foreground">{t('anatel:conselheiro.conselho.styleTitle')}</h3>
        <Answer answer={conselho.rubric} help={t('anatel:conselheiro.conselho.rubricHelp')} scope="record">
          {(rows) => <RubricTable rows={rows} showMember={false} />}
        </Answer>
        <RunFooter run={conselho.rubric_run} />
        <h3 className="mb-2 mt-3 text-xs font-semibold text-foreground">{t('anatel:conselho.style.residualTitle')}</h3>
        <Answer answer={conselho.style_residual} help={t('anatel:conselho.style.residualHelp')} scope="record">
          {(rows) => (
            <IntervalList
              rows={metricRows(rows).map((r) => ({ ...r, name: scopeLabel(r.name) }))}
              axisLabel={t('anatel:conselho.style.residualAxis')}
              format={logit}
              rawFormat={(v) => percent.format(v)}
              rawLabel={t('anatel:conselho.style.favorableShare')}
            />
          )}
        </Answer>
        <RunFooter run={conselho.projection_run} />
        <h3 className="mb-2 mt-3 text-xs font-semibold text-foreground">{t('anatel:conselho.style.wordfishTitle')}</h3>
        <Answer answer={conselho.wordfish} help={t('anatel:conselho.style.wordfishHelp')} scope="record">
          {(rows) => (
            <IntervalList rows={metricRows(rows).map((r) => ({ ...r, name: scopeLabel(r.name) }))} axisLabel={t('anatel:conselho.style.wordfishAxis')} format={logit} />
          )}
        </Answer>
        <RunFooter run={conselho.wordfish_run} />
      </div>

      {conselho.contested_items.length > 0 ? (
        <div data-panel="conselho-items">
          <h3 className="mb-1 text-xs font-semibold text-foreground">{t('anatel:conselheiro.conselho.itemsTitle')}</h3>
          <p className="mb-2 text-[11px] text-muted-foreground">{t('anatel:conselheiro.conselho.itemsHelp')}</p>
          <ContestedItemsTable items={conselho.contested_items} showSide />
        </div>
      ) : null}

      {conselho.documents.length > 0 ? (
        <div data-panel="conselho-documents">
          <h3 className="mb-1 text-xs font-semibold text-foreground">{t('anatel:conselheiro.conselho.documentsTitle')}</h3>
          <p className="mb-2 text-[11px] text-muted-foreground">{t('anatel:conselheiro.conselho.documentsHelp')}</p>
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-xs" data-testid="authored-documents">
              <thead className="sticky top-0 z-10 bg-muted text-left">
                <tr>
                  <th className="p-2">{t('anatel:conselheiro.conselho.docKind')}</th>
                  <th className="p-2">{t('anatel:conselheiro.conselho.docNumber')}</th>
                  <th className="p-2">{t('anatel:conselheiro.conselho.docDate')}</th>
                  <th className="p-2">{t('anatel:conselheiro.conselho.docProcess')}</th>
                  <th className="p-2">{t('anatel:conselheiro.conselho.votoType')}</th>
                  <th className="p-2">{t('anatel:conselheiro.conselho.linked')}</th>
                </tr>
              </thead>
              <tbody>
                {conselho.documents.map((d, index) => (
                  <tr key={`${d.doc_number ?? 'n'}-${index}`} className="border-t">
                    <td className="p-2">{d.doc_kind}</td>
                    <td className="p-2 tabular-nums">{d.doc_number ?? '—'}</td>
                    <td className="p-2 tabular-nums text-muted-foreground">{d.document_date ?? '—'}</td>
                    <td className="p-2 tabular-nums">
                      {d.process_key ? (
                        <Link to={`/anatel/p/${d.process_key}`} className="text-primary underline-offset-4 hover:underline">
                          {d.process_number}
                        </Link>
                      ) : (
                        (d.process_number ?? '—')
                      )}
                    </td>
                    <td className="p-2 text-muted-foreground">{d.voto_type ?? '—'}</td>
                    <td className="p-2 text-muted-foreground">
                      {d.linked ? t('anatel:conselheiro.conselho.linked') : t('anatel:conselheiro.conselho.unlinked')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </section>
  );
}
